/**
 * Theme CSS Utilities
 *
 * DOM manipulation utilities for theme application.
 * Web-only utilities for applying theme classes and CSS variables.
 */

import type { Theme, IOrganizationThemeColors, IOrganizationThemeAssets } from './types';

// ============================================================================
// CONSTANTS
// ============================================================================

/** CSS custom properties that can be customized */
const CUSTOMIZABLE_CSS_PROPERTIES = [
  // Brand
  '--primary',
  '--primary-foreground',
  '--secondary',
  '--secondary-foreground',
  '--accent',
  '--accent-foreground',
  // Semantic
  '--success',
  '--success-foreground',
  '--accent-green',
  '--warning',
  '--warning-foreground',
  '--accent-orange',
  '--error',
  '--destructive',
  '--destructive-foreground',
  // Surface
  '--background',
  '--foreground',
  '--card',
  '--card-foreground',
  '--muted',
  '--muted-foreground',
  '--border',
  // Popover
  '--popover',
  '--popover-foreground',
  // Input & focus
  '--input',
  '--ring',
  // Sidebar
  '--sidebar',
  '--sidebar-foreground',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-accent',
  '--sidebar-accent-foreground',
  '--sidebar-border',
  '--sidebar-ring',
  // Charts
  '--chart-1',
] as const;

// ============================================================================
// SYSTEM THEME DETECTION
// ============================================================================

/**
 * Get the system's preferred color scheme
 */
export function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Subscribe to system theme changes
 * Returns cleanup function
 */
export function subscribeToSystemTheme(callback: (theme: Theme) => void): () => void {
  if (typeof window === 'undefined') {
    // Return no-op cleanup function for SSR
    return function noop() {
      /* no-op */
    };
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent) => {
    callback(e.matches ? 'dark' : 'light');
  };

  mediaQuery.addEventListener('change', handler);
  return () => mediaQuery.removeEventListener('change', handler);
}

// ============================================================================
// THEME CLASS APPLICATION
// ============================================================================

/**
 * Apply theme class to document root
 */
export function applyThemeClass(theme: Theme): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Get current theme from document class
 */
export function getCurrentThemeClass(): Theme {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

// ============================================================================
// COLOR DERIVATION HELPERS
// ============================================================================

/**
 * Parse a hex color to RGB components.
 * Supports #RGB, #RRGGBB formats.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '');
  if (clean.length === 3) {
    return {
      r: parseInt(clean[0] + clean[0], 16),
      g: parseInt(clean[1] + clean[1], 16),
      b: parseInt(clean[2] + clean[2], 16),
    };
  }
  if (clean.length === 6) {
    return {
      r: parseInt(clean.substring(0, 2), 16),
      g: parseInt(clean.substring(2, 4), 16),
      b: parseInt(clean.substring(4, 6), 16),
    };
  }
  return null;
}

/**
 * Compute relative luminance (WCAG 2.0).
 * Returns 0 (black) to 1 (white).
 */
function luminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns white or black hex depending on which has better contrast
 * against the given background color.
 */
function contrastForeground(bgHex: string): string {
  const rgb = hexToRgb(bgHex);
  if (!rgb) return '#ffffff';
  return luminance(rgb.r, rgb.g, rgb.b) > 0.4 ? '#1f2937' : '#ffffff';
}

/**
 * Mix two hex colors. factor=0 → colorA, factor=1 → colorB.
 */
function mixColors(colorA: string, colorB: string, factor: number): string {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  if (!a || !b) return colorA;
  const mix = (ca: number, cb: number) => Math.round(ca + (cb - ca) * factor);
  const r = mix(a.r, b.r);
  const g = mix(a.g, b.g);
  const bl = mix(a.b, b.b);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

/**
 * Check if a string looks like a hex color we can derive from.
 */
function isHexColor(value: string | undefined): value is string {
  if (!value) return false;
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/**
 * Derive a full set of CSS variable values from base colors.
 * Fills in missing values using smart derivation from provided base colors.
 */
function deriveThemeColors(colors: IOrganizationThemeColors): Record<string, string | undefined> {
  const bg = colors.background;
  const fg = colors.foreground;
  const primary = colors.primary;
  const secondary = colors.secondary;
  const border = colors.border;

  // Auto-derive foreground variants for brand colors
  const primaryFg =
    colors.primaryForeground ?? (isHexColor(primary) ? contrastForeground(primary) : undefined);
  const secondaryFg =
    colors.secondaryForeground ??
    (isHexColor(secondary) ? contrastForeground(secondary) : undefined);
  const accentFg =
    colors.accentForeground ??
    (isHexColor(colors.accent) ? contrastForeground(colors.accent) : undefined);

  // Card defaults to background
  const card = colors.card ?? bg;
  const cardFg = colors.cardForeground ?? fg;

  // Popover defaults to card/background
  const popover = colors.popover ?? card ?? bg;
  const popoverFg = colors.popoverForeground ?? cardFg ?? fg;

  // Muted: if not provided, derive from background (slightly darker/lighter)
  const muted =
    colors.muted ?? (isHexColor(bg) && isHexColor(fg) ? mixColors(bg, fg, 0.06) : undefined);
  const mutedFg =
    colors.mutedForeground ??
    (isHexColor(bg) && isHexColor(fg) ? mixColors(fg, bg, 0.35) : undefined);

  // Border: if not provided, derive from background (slightly darker)
  const derivedBorder =
    border ?? (isHexColor(bg) && isHexColor(fg) ? mixColors(bg, fg, 0.12) : undefined);

  // Input matches border
  const input = colors.input ?? derivedBorder;

  // Ring matches primary
  const ring = colors.ring ?? primary;

  // Sidebar: derive from background (slightly different shade)
  const sidebar =
    colors.sidebar ?? (isHexColor(bg) && isHexColor(fg) ? mixColors(bg, fg, 0.02) : undefined);
  const sidebarFg = colors.sidebarForeground ?? fg;
  const sidebarPrimary = colors.sidebarPrimary ?? primary;
  const sidebarPrimaryFg = colors.sidebarPrimaryForeground ?? primaryFg;
  const sidebarAccent = colors.sidebarAccent ?? secondary;
  const sidebarAccentFg =
    colors.sidebarAccentForeground ??
    (isHexColor(secondary) ? contrastForeground(secondary) : undefined);
  const sidebarBorder = colors.sidebarBorder ?? derivedBorder;
  const sidebarRing = colors.sidebarRing ?? ring;

  return {
    // Brand
    '--primary': primary,
    '--primary-foreground': primaryFg,
    '--secondary': secondary,
    '--secondary-foreground': secondaryFg,
    '--accent': colors.accent,
    '--accent-foreground': accentFg,

    // Semantic
    '--success': colors.success,
    '--success-foreground': isHexColor(colors.success)
      ? contrastForeground(colors.success)
      : undefined,
    '--accent-green': colors.success,
    '--warning': colors.warning,
    '--warning-foreground': isHexColor(colors.warning)
      ? contrastForeground(colors.warning)
      : undefined,
    '--accent-orange': colors.warning,
    '--error': colors.error,
    '--destructive': colors.error,
    '--destructive-foreground': isHexColor(colors.error)
      ? contrastForeground(colors.error)
      : undefined,

    // Surface
    '--background': bg,
    '--foreground': fg,
    '--card': card,
    '--card-foreground': cardFg,
    '--muted': muted,
    '--muted-foreground': mutedFg,
    '--border': derivedBorder,

    // Popover
    '--popover': popover,
    '--popover-foreground': popoverFg,

    // Input & focus
    '--input': input,
    '--ring': ring,

    // Sidebar
    '--sidebar': sidebar,
    '--sidebar-foreground': sidebarFg,
    '--sidebar-primary': sidebarPrimary,
    '--sidebar-primary-foreground': sidebarPrimaryFg,
    '--sidebar-accent': sidebarAccent,
    '--sidebar-accent-foreground': sidebarAccentFg,
    '--sidebar-border': sidebarBorder,
    '--sidebar-ring': sidebarRing,

    // Charts (derive from brand/semantic palette)
    '--chart-1': primary,
  };
}

// ============================================================================
// CUSTOM COLOR APPLICATION
// ============================================================================

/**
 * Clear all custom CSS properties
 */
export function clearCustomColors(): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  CUSTOMIZABLE_CSS_PROPERTIES.forEach((prop) => {
    root.style.removeProperty(prop);
  });
}

/**
 * Apply custom CSS variables from organization theme colors.
 * Uses smart derivation to fill in missing values from base colors.
 */
export function applyCustomColors(colors: IOrganizationThemeColors | undefined): void {
  if (typeof document === 'undefined') return;

  // Always clear first to ensure clean state
  clearCustomColors();

  if (!colors) return;

  const root = document.documentElement;

  // Derive all CSS variables from the base colors
  const derived = deriveThemeColors(colors);

  // Apply each color that has a value
  Object.entries(derived).forEach(([property, value]) => {
    if (value) {
      root.style.setProperty(property, value);
    }
  });
}

// ============================================================================
// CUSTOM ASSETS APPLICATION
// ============================================================================

/**
 * Apply custom logo assets
 * Uses data attributes to identify logo elements
 */
export function applyCustomAssets(assets: IOrganizationThemeAssets | undefined): void {
  if (typeof document === 'undefined' || !assets) return;

  // Apply primary logo to elements with data-logo="primary"
  if (assets.primaryLogo) {
    const primaryLogoUrl = assets.primaryLogo;
    const elements = document.querySelectorAll('[data-logo="primary"]');
    elements.forEach((el) => {
      if (el instanceof HTMLImageElement) {
        el.src = primaryLogoUrl;
      }
    });
  }

  // Apply secondary logo to elements with data-logo="secondary"
  if (assets.secondaryLogo) {
    const secondaryLogoUrl = assets.secondaryLogo;
    const elements = document.querySelectorAll('[data-logo="secondary"]');
    elements.forEach((el) => {
      if (el instanceof HTMLImageElement) {
        el.src = secondaryLogoUrl;
      }
    });
  }

  // Apply favicon
  if (assets.favicon) {
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon instanceof HTMLLinkElement) {
      favicon.href = assets.favicon;
    }
  }
}

// ============================================================================
// COLOR SCHEME META TAG
// ============================================================================

/**
 * Update the color-scheme meta tag for browser UI
 */
export function updateColorSchemeMeta(theme: Theme): void {
  if (typeof document === 'undefined') return;

  let meta = document.querySelector('meta[name="color-scheme"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'color-scheme');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', theme);
}
